import { __awaiter, __rest } from '../../../../../_virtual/_tslib.js';
import { usePromise } from '../usePromise/usePromise.js';
import '@dynamic-labs/sdk-api';
import { logger } from '../../../shared/logger.js';
import '@dynamic-labs/iconic';
import '@dynamic-labs/wallet-connector-core';
import 'react/jsx-runtime';
import '../../../context/ViewContext/ViewContext.js';
import 'react';
import '@dynamic-labs/wallet-book';
import '@dynamic-labs/utils';
import '../../constants/colors.js';
import '../../../shared/utils/classes/storage/localStorage.js';
import 'ethers';
import '../../../shared/consts/index.js';

/**
 * Hook to estimate the gas required for a transaction.
 *
 * This hook uses the `usePromise` hook to asynchronously estimate the gas required for a transaction.
 * It first tries to estimate the gas without the 'from' field in the transaction, as the Alchemy API
 * checks the origin address funds and can return an error if the origin wallet has no funds for the transaction.
 * If the error 'UNPREDICTABLE_GAS_LIMIT' is encountered, it tries to estimate the gas again with the
 * original transaction.
 *
 * @see {@link https://docs.alchemy.com/reference/eth-estimategas}
 * @see {@link https://docs.ethers.org/v5/troubleshooting/errors/#help-UNPREDICTABLE_GAS_LIMIT}
 *
 * @param {ethers.providers.Web3Provider} provider - The Web3 provider to use for estimating the gas.
 * @param {ethers.utils.Deferrable<ethers.providers.TransactionRequest>} transaction - The transaction for
 * which to estimate the gas.
 *
 * @returns {PromiseState<ethers.BigNumber>} A promise that resolves to the estimated gas for the transaction.
 */
const useTransactionEstimatedGas = (provider, transaction) => usePromise(() => __awaiter(void 0, void 0, void 0, function* () {
    // To safely run estimateGas using alchemy, we need to remove the from address
    // because alchemy checks the origin address funds and can return an error if
    // origin wallet has no funds for the transaction
    // https://docs.alchemy.com/reference/eth-estimategas
    const transactionWithNoOrigin = __rest(transaction, ["from"]);
    try {
        return yield provider.estimateGas(transactionWithNoOrigin);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }
    catch (err) {
        logger.error(err);
        // If the error is UNPREDICTABLE_GAS_LIMIT, we try to estimate gas again
        // with original transaction
        // https://docs.ethers.org/v5/troubleshooting/errors/#help-UNPREDICTABLE_GAS_LIMIT
        if (err.code === 'UNPREDICTABLE_GAS_LIMIT') {
            return provider.estimateGas(transaction);
        }
        throw err;
    }
}), { deps: [transaction, provider] });

export { useTransactionEstimatedGas };
